/**
 * CostBreakdownPanel — Stacked bar + table showing cost allocation by
 * resource category for the Budget subtab within Performance.
 *
 * Reads PreflightItems from the in-memory store, groups by cost_category,
 * renders a 100% stacked horizontal bar with labels baked into large
 * segments, and a compact table below with color-keyed rows.
 *
 * Color palette deliberately avoids blue/green/yellow/red which carry
 * reserved status meanings on Management/Performance surfaces.
 *
 * Standard tier — no Gantt/EVM dependency.
 */

import React, { useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { preflightStore, storeKey } from "../../Data/store";
import { usePreflightLoader } from "../../hooks/usePreflightLoader";
import type { PreflightItem, ResourceCategory } from "../../Data/PreFlight/preflightTypes";
import {
  RESOURCE_CATEGORIES,
  normalizeCategory,
} from "../../Data/PreFlight/preflightTypes";
import { formatCurrencyCompact } from "../../Data/Budget/currencyLookup";
import styles from "./Navigation.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface CostBreakdownPanelProps {
  projectGuid: string;
  budgetCurrency: string;
  debugMode?: boolean;
  /** Callback to expose totalEstimated to parent for funding coverage calc */
  onTotalEstimated?: (total: number) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

interface CategoryRow {
  category: ResourceCategory;
  estimated: number;
  actual: number;
  count: number;
}

/**
 * Categorical palette — avoids reserved Management-tab status colors:
 *   blue  = Complete/Done
 *   green = OK / On-track
 *   yellow = Warning
 *   red   = Critical/Blocked
 *
 * Uses purples, teals, corals, pinks, and warm neutrals instead.
 */
const CATEGORY_COLORS = [
  "hsl(270, 50%, 58%)",  // purple
  "hsl(175, 50%, 42%)",  // teal
  "hsl(20,  65%, 55%)",  // coral / burnt orange
  "hsl(325, 45%, 55%)",  // pink-magenta
  "hsl(195, 40%, 50%)",  // steel / slate blue
  "hsl(35,  55%, 50%)",  // warm tan
  "hsl(290, 35%, 50%)",  // plum
  "hsl(160, 35%, 45%)",  // sage-teal
  "hsl(15,  50%, 48%)",  // sienna
  "hsl(340, 40%, 52%)",  // rose
  "hsl(245, 30%, 52%)",  // indigo-grey
  "hsl(50,  35%, 48%)",  // olive
  "hsl(0,   0%,  52%)",  // neutral grey
];

/** Text color for labels rendered inside stacked-bar segments. */
const SEGMENT_TEXT_COLORS = [
  "hsl(270, 80%, 92%)",  // on purple
  "hsl(175, 60%, 90%)",  // on teal
  "hsl(20,  80%, 92%)",  // on coral
  "hsl(325, 60%, 92%)",  // on pink
  "hsl(195, 50%, 90%)",  // on steel
  "hsl(35,  60%, 92%)",  // on tan
  "hsl(290, 50%, 90%)",  // on plum
  "hsl(160, 50%, 90%)",  // on sage
  "hsl(15,  60%, 90%)",  // on sienna
  "hsl(340, 50%, 90%)",  // on rose
  "hsl(245, 40%, 90%)",  // on indigo
  "hsl(50,  40%, 18%)",  // on olive (dark text)
  "hsl(0,   0%,  92%)",  // on grey
];

/** Minimum segment width (%) to show an inline label */
const MIN_LABEL_PCT = 12;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CostBreakdownPanel: React.FC<CostBreakdownPanelProps> = ({
  projectGuid,
  budgetCurrency,
  debugMode,
  onTotalEstimated,
}) => {
  const { t: tKpi } = useAppTranslation("kpi");

  /* ── Load preflight items ── */
  const { preflightVersion } = usePreflightLoader(projectGuid, true);

  const items: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    return preflightStore.get(storeKey(projectGuid)) || [];
  }, [projectGuid, preflightVersion]);

  /* ── Rollup by category ── */
  const { byCategory, totalEstimated, totalActual } = useMemo(() => {
    const catMap: Record<string, CategoryRow> = {};
    for (const cc of RESOURCE_CATEGORIES) {
      catMap[cc] = { category: cc, estimated: 0, actual: 0, count: 0 };
    }

    let totalEst = 0;
    let totalAct = 0;

    for (const item of items) {
      if (item.state === "withdrawn") continue;

      const est = item.estimated_total ?? (
        item.unit_cost != null && item.target_quantity != null
          ? item.unit_cost * item.target_quantity
          : 0
      );
      const act = item.actual_total ?? (
        item.actual_cost != null
          ? item.actual_cost * item.current_quantity
          : 0
      );

      const cc = normalizeCategory(item.category);
      if (!catMap[cc]) catMap[cc] = { category: cc as ResourceCategory, estimated: 0, actual: 0, count: 0 };
      catMap[cc].estimated += est;
      catMap[cc].actual += act;
      catMap[cc].count++;

      totalEst += est;
      totalAct += act;
    }

    const byCat = Object.values(catMap)
      .filter((r) => r.count > 0)
      .sort((a, b) => b.estimated - a.estimated);

    return {
      byCategory: byCat,
      totalEstimated: totalEst,
      totalActual: totalAct,
    };
  }, [items]);

  /* ── Expose total to parent ── */
  React.useEffect(() => {
    onTotalEstimated?.(totalEstimated);
  }, [totalEstimated, onTotalEstimated]);

  /* ── Empty state ── */
  if (byCategory.length === 0) {
    return (
      <div className={styles.breakdownPanel}>
        <div className={styles.breakdownTitle}>
          {tKpi("budget.breakdown.title")}
        </div>
        <div className={styles.breakdownEmpty}>
          {tKpi("budget.breakdown.empty")}
        </div>
      </div>
    );
  }

  /* ── Render ── */
  const spendPct = totalEstimated > 0
    ? Math.min(100, Math.round((totalActual / totalEstimated) * 100))
    : 0;

  return (
    <div className={styles.breakdownPanel}>
      {/* Section header */}
      <div className={styles.breakdownTitle}>
        {tKpi("budget.breakdown.title")}
      </div>

      {/* Metric cards */}
      <div className={styles.breakdownMetricRow}>
        <div className={styles.breakdownMetricCard}>
          <span className={styles.breakdownMetricLabel}>
            {tKpi("budget.breakdown.estimated")}
          </span>
          <span className={styles.breakdownMetricValue}>
            {formatCurrencyCompact(totalEstimated, budgetCurrency)}
          </span>
        </div>
        <div className={styles.breakdownMetricCard}>
          <span className={styles.breakdownMetricLabel}>
            {tKpi("budget.breakdown.actual")}
          </span>
          <span className={styles.breakdownMetricValue}>
            {formatCurrencyCompact(totalActual, budgetCurrency)}
          </span>
        </div>
      </div>

      {/* Stacked bar */}
      <div className={styles.stackedBar}>
        {byCategory.map((row, idx) => {
          const pct = totalEstimated > 0
            ? (row.estimated / totalEstimated) * 100
            : 0;
          if (pct < 0.5) return null;
          return (
            <div
              key={row.category}
              className={styles.stackedBarSegment}
              style={{
                width: `${pct}%`,
                background: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
              }}
            >
              {pct >= MIN_LABEL_PCT && (
                <span
                  className={styles.stackedBarSegmentLabel}
                  style={{ color: SEGMENT_TEXT_COLORS[idx % SEGMENT_TEXT_COLORS.length] }}
                >
                  {tKpi(`budget.breakdown.categories.${row.category}`)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Table */}
      <table className={styles.breakdownTable}>
        <thead>
          <tr>
            <th className={styles.breakdownTh} style={{ textAlign: "start" }}></th>
            <th className={styles.breakdownTh}>{tKpi("budget.breakdown.estAbbr")}</th>
            <th className={styles.breakdownTh}>{tKpi("budget.breakdown.actAbbr")}</th>
            <th className={styles.breakdownTh} style={{ width: 30 }}>%</th>
          </tr>
        </thead>
        <tbody>
          {byCategory.map((row, idx) => {
            const pct = totalEstimated > 0
              ? Math.round((row.estimated / totalEstimated) * 100)
              : 0;
            return (
              <tr key={row.category} className={styles.breakdownTr}>
                <td className={styles.breakdownTdName}>
                  <span
                    className={styles.breakdownColorDot}
                    style={{ background: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] }}
                  />
                  {tKpi(`budget.breakdown.categories.${row.category}`)}
                </td>
                <td className={styles.breakdownTdNum}>
                  {formatCurrencyCompact(row.estimated, budgetCurrency)}
                </td>
                <td className={styles.breakdownTdNum}>
                  {formatCurrencyCompact(row.actual, budgetCurrency)}
                </td>
                <td className={styles.breakdownTdPct}>{pct}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CostBreakdownPanel;
